import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const { FiCheckCircle, FiClock, FiCircle, FiTrendingUp, FiList, FiArrowRight, FiActivity, FiCheck, FiChevronDown, FiChevronRight } = FiIcons;

const TeamImprovements = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inProgressRecs, setInProgressRecs] = useState([]);
  const [doneHistory, setDoneHistory] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (user?.teamId) fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Get Team Used Recs
      const teamRecsRes = await apiService.getTeamUsedRecommendations({ team_id: user.teamId, limit: 1000 });
      const teamRecs = teamRecsRes.data || [];

      if (teamRecs.length === 0) {
        setInProgressRecs([]);
        setDoneHistory([]);
        setHistory([]);
        setLoading(false);
        return;
      }

      // 2. Get Rec Details
      const allRecsRes = await apiService.getRecommendations({ limit: 1000 });
      const allRecs = allRecsRes.data || [];

      // 3. Map details
      const enrichedRecs = teamRecs.map(tr => {
        const rec = allRecs.find(r => r.id === tr.recommendation_id);
        // Note: Spread order matters. We want to ensure recommendation_id is preserved from tr if needed, 
        // but rec.id should match it.
        return { ...tr, ...rec, recommendation_id: tr.recommendation_id }; 
      });

      // 4. Filter In Progress (Status 1)
      const inProgress = enrichedRecs.filter(r => String(r.in_progress_used) === '1');
      setInProgressRecs(inProgress);

      // 5. Get History (Status Changes)
      const changesRes = await apiService.getRecommendationStatusChanges({ limit: 1000 });
      const allChanges = changesRes.data || [];

      const teamRecIds = enrichedRecs.map(r => r.recommendation_id);
      const relevantChanges = allChanges.filter(c => teamRecIds.includes(c.recommendation_id));
      
      // Enrich changes with Rec titles
      const historyData = relevantChanges.map(c => {
         const rec = allRecs.find(r => r.id === c.recommendation_id);
         return { ...c, recTitle: rec?.title };
      });

      // Sort by date desc (newest first)
      historyData.sort((a, b) => new Date(b.datetime) - new Date(a.datetime));

      setHistory(historyData);

      // 6. Filter Done History (Only currently Implemented Recs)
      // Identify IDs of recommendations that are currently Status 2
      const currentDoneRecIds = enrichedRecs
        .filter(r => String(r.in_progress_used) === '2')
        .map(r => r.recommendation_id);

      const doneEvents = [];
      const processedDoneIds = new Set();

      // Iterate history (newest first) to find the latest 'set to 2' event for each currently done rec
      for (const event of historyData) {
        if (
             String(event.status_change_end) === '2' && 
             currentDoneRecIds.includes(event.recommendation_id) &&
             !processedDoneIds.has(event.recommendation_id)
           ) {
             doneEvents.push(event);
             processedDoneIds.add(event.recommendation_id);
        }
      }

      setDoneHistory(doneEvents);

    } catch (e) {
      console.error("Error fetching improvements", e);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const s = String(status);
    if (s === '0') return 'OFFEN';
    if (s === '1') return 'IN PROGRESS';
    if (s === '2') return 'UMGESETZT';
    return 'UNBEKANNT';
  };

  const getStatusColor = (status) => {
     const s = String(status);
     if (s === '0') return 'text-gray-500 bg-gray-100 border-gray-200';
     if (s === '1') return 'text-blue-600 bg-blue-50 border-blue-200';
     if (s === '2') return 'text-green-600 bg-green-50 border-green-200';
     return 'text-gray-500 bg-gray-100';
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return dateStr;
    }
  };
  
  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  if (loading) {
     return (
       <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
       </div>
     );
  }

  return (
    <div className="space-y-12 animate-fadeIn">
       
       {/* Section 1: In Progress */}
       <div className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
             <div className="p-2 bg-blue-100 rounded-full">
                <SafeIcon icon={FiActivity} className="text-blue-600 w-5 h-5" />
             </div>
             <div>
                <h3 className="text-lg font-bold text-gray-800">Aktuell in Bearbeitung</h3>
                <p className="text-sm text-gray-500">Maßnahmen, an denen das Team gerade arbeitet</p>
             </div>
          </div>
          
          {inProgressRecs.length === 0 ? (
             <div className="p-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-gray-400">
                Aktuell keine Maßnahmen im Status "In Progress".
             </div>
          ) : (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {inProgressRecs.map(rec => (
                   <div key={rec.id} className="bg-white p-5 rounded-lg border-l-4 border-blue-500 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
                      <div>
                         <div className="flex justify-between items-start mb-3">
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded border text-blue-600 bg-blue-50 border-blue-200 uppercase tracking-wide">
                               IN PROGRESS
                            </span>
                         </div>
                         <h4 className="font-bold text-gray-900 mb-2 text-lg">{rec.title}</h4>
                         <p className="text-sm text-gray-600 line-clamp-2">{rec.short_description || rec.description}</p>
                      </div>
                   </div>
                ))}
             </div>
          )}
       </div>

       {/* Section 2: Timeline (Done) */}
       <div className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
             <div className="p-2 bg-green-100 rounded-full">
                <SafeIcon icon={FiCheckCircle} className="text-green-600 w-5 h-5" />
             </div>
             <div>
                <h3 className="text-lg font-bold text-gray-800">Erfolgs-Timeline</h3>
                <p className="text-sm text-gray-500">Erfolgreich umgesetzte Maßnahmen</p>
             </div>
          </div>

          {doneHistory.length === 0 ? (
             <div className="p-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-gray-400">
                Noch keine Maßnahmen abgeschlossen.
             </div>
          ) : (
             <div className="relative pl-8 border-l-2 border-green-200 space-y-8 ml-4">
                {doneHistory.map((item, index) => (
                   <div key={item.id || index} className="relative">
                      {/* Dot on timeline */}
                      <div className="absolute -left-[41px] top-1 h-5 w-5 rounded-full bg-green-500 border-4 border-white shadow-sm flex items-center justify-center">
                         <SafeIcon icon={FiCheck} className="text-white w-3 h-3" />
                      </div>
                      
                      {/* Content */}
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all">
                         <span className="text-xs font-semibold text-green-600 mb-1 block">
                            {formatDateOnly(item.datetime)}
                         </span>
                         <h4 className="font-bold text-gray-900">{item.recTitle || `Empfehlung #${item.recommendation_id}`}</h4>
                         <p className="text-xs text-gray-500 mt-1">Status geändert auf Umgesetzt</p>
                      </div>
                   </div>
                ))}
             </div>
          )}
       </div>

       {/* Section 3: Full History (Collapsible) */}
       <div className="space-y-4 pt-4 border-t border-gray-200">
          <button 
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 w-full text-left group focus:outline-none"
          >
             <div className="p-1 bg-gray-100 rounded group-hover:bg-gray-200 transition-colors">
                <SafeIcon icon={showHistory ? FiChevronDown : FiChevronRight} className="text-gray-600 w-4 h-4" />
             </div>
             <div className="flex items-center gap-2">
                <SafeIcon icon={FiList} className="text-gray-500 w-5 h-5" />
                <h3 className="text-lg font-bold text-gray-800">Detaillierte Historie</h3>
             </div>
             <span className="text-xs text-gray-400 ml-auto">
                {showHistory ? 'Ausblenden' : 'Anzeigen'}
             </span>
          </button>

          {showHistory && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden animate-fadeIn">
               {history.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                     Noch keine Statusänderungen verzeichnet.
                  </div>
               ) : (
                  <div className="divide-y divide-gray-100 max-h-[900px] overflow-y-auto">
                     {history.map((item, index) => (
                        <div key={item.id || index} className="p-3 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                           <div className="text-xs text-gray-400 font-mono w-24 shrink-0">
                              {formatDateTime(item.datetime)}
                           </div>
                           <div className="flex-1">
                              <p className="font-medium text-gray-800 text-sm">{item.recTitle || `Empfehlung #${item.recommendation_id}`}</p>
                              <div className="flex items-center gap-2 mt-1 text-xs">
                                 <span className={`px-1.5 py-0.5 rounded border ${getStatusColor(item.status_change_start)}`}>
                                    {getStatusLabel(item.status_change_start)}
                                 </span>
                                 <SafeIcon icon={FiArrowRight} className="text-gray-400 w-3 h-3" />
                                 <span className={`px-1.5 py-0.5 rounded border ${getStatusColor(item.status_change_end)}`}>
                                    {getStatusLabel(item.status_change_end)}
                                 </span>
                              </div>
                           </div>
                        </div>
                     ))}
                  </div>
               )}
            </div>
          )}
       </div>

    </div>
  );
};

export default TeamImprovements;